import {
  CarouselWithTitle,
  ICarouselWithTitleProps,
} from "@faire/retailer-visitor-shared/components/Carousel/CarouselWithTitle";
import { HorizontalScrollCarousel } from "@faire/retailer-visitor-shared/components/Carousel/HorizontalScrollCarousel";
import { useIsLoggedOutSuppliersLayoutElementLoading } from "@faire/retailer-visitor-shared/views/suppliers/__internal__/LazySuppliersPage/__internal__/SuppliersPage/__internal__/DataFetching/LoggedOutSuppliersLayoutElement/useIsLoggedOutSuppliersLayoutElementLoading";
import { LoggedOutSuppliersPageHeaderCategoryTile } from "@faire/retailer-visitor-shared/views/suppliers/__internal__/LazySuppliersPage/__internal__/SuppliersPage/__internal__/LoggedOutSuppliersPage/__internal__/Components/LoggedOutSuppliersPageHeader/__internal__/LoggedOutSuppliersPageHeaderCategoryTile";
import { useHeaderCarouselCategoriesToDisplay } from "@faire/retailer-visitor-shared/views/suppliers/__internal__/LazySuppliersPage/__internal__/SuppliersPage/__internal__/LoggedOutSuppliersPage/__internal__/useHeaderCarouselCategoriesToDisplay";
import { trackSeoSuppliersPageVisualNavCarouselMobileScroll } from "@faire/web-api--source/events/seo/actionCategoryOther/suppliersPageVisualNavCarouselMobileScroll";
import { trackSeoSuppliersPageVisualNavCarouselMobileScrollEnd } from "@faire/web-api--source/events/seo/actionCategoryOther/suppliersPageVisualNavCarouselMobileScrollEnd";
import { trackSeoSuppliersPageVisualNavCarouselView } from "@faire/web-api--source/events/seo/view/suppliersPageVisualNavCarousel";
import { ICategoryTile } from "@faire/web-api--source/indigofair/data/ICategoryTile";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { useResponsiveValue } from "@faire/web/ui/hooks/useResponsiveValue";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import React, { ComponentType, useCallback } from "react";
import styled from "styled-components";

interface IProps {
  categoryPathIdentifier?: string;
  title?: string;
  className?: string;
}

const MAX_ATTRIBUTE_ON_DESKTOP = 3;
const MAX_ATTRIBUTE_ON_MOBILE = 1;

export const LoggedOutSuppliersPageHeaderCategoryCarousel: React.FC<IProps> = ({
  categoryPathIdentifier,
  title,
  className,
}) => {
  const { isMobile, isTabletAndBelow } = useViewport();
  const isLoading = useIsLoggedOutSuppliersLayoutElementLoading();

  const categoriesToDisplay = useHeaderCarouselCategoriesToDisplay();

  const onView = useCallback(() => {
    trackSeoSuppliersPageVisualNavCarouselView(categoryPathIdentifier ?? "");
  }, [categoryPathIdentifier]);

  const viewRef = useDoActionWhenVisible(onView);

  const peekGapPercent = useResponsiveValue({
    mobileAndAbove: 50,
    tabletAndAbove: 20,
    desktopAndAbove: 10,
  });

  if (categoriesToDisplay.length === 0) {
    return null;
  }

  const renderCategoryTile = (category: ICategoryTile, index: number) => {
    return (
      <LoggedOutSuppliersPageHeaderCategoryTile
        key={index}
        category={category}
        isCategoryLoading={isLoading}
      />
    );
  };
  return (
    <div ref={viewRef}>
      <StyledCarousel
        className={className}
        title={title ?? ""}
        leftAlignTitle
        noLeftTitlePadding
        titleTypography="sectionHeaderSansMedium"
        name="logged-out-suppliers-page-category-carousel"
        items={categoriesToDisplay}
        itemGap={16}
        peekNext
        noWrap
        itemsPerSlide={
          isMobile ? MAX_ATTRIBUTE_ON_MOBILE : MAX_ATTRIBUTE_ON_DESKTOP
        }
        padItems
        renderItem={renderCategoryTile}
        peekGapPercent={peekGapPercent}
        lateralPadding={isTabletAndBelow ? 0 : 24}
        useHorizontalScrollCarousel={isTabletAndBelow}
        alwaysIncludePeek
        onMobileScroll={trackSeoSuppliersPageVisualNavCarouselMobileScroll}
        onMobileScrollEnd={
          trackSeoSuppliersPageVisualNavCarouselMobileScrollEnd
        }
      />
    </div>
  );
};

const StyledCarousel = styled(
  CarouselWithTitle as ComponentType<ICarouselWithTitleProps<ICategoryTile>>
)`
  ${HorizontalScrollCarousel.Item} {
    max-width: 202px;
  }
`;
